import {useMessages} from "i18n";
import {PeelTicket} from "peel/PeelTicket";
import type {PaidPlanId} from "pricing/planCatalogue";
import {countdownDaysLabel, type CampaignCountdown} from "./campaignCountdown";
import type {OfferFirstMonthFigures} from "./firstMonthPricing";

/**
 * A first-month offer printed on the coupon's ticket (owner 2026-09-27, ui#2949): the success card's
 * campaign and the trial-expiry offer. They are offers like the coupon, so they read as one.
 *
 * The same presentation the coupon settled on (ui#2545): the ticket prints the first-month amount and
 * no percentage, and the heading's accessible name carries the whole sentence with the renewal price,
 * because the ticket has room for one figure. The deadline and the remaining places print where the
 * coupon prints its own, in the campaign's own words. Markup only; every figure is handed in.
 *
 * @param title  the heading's accessible name: the surface's own sentence naming both prices
 * @param testId      the surface's id
 * @param factsTestId the prefix of its two facts' ids (`-deadline`, `-spots-left`), which the surfaces
 *                    named before they shared this ticket and their specs still address
 */
export function FirstMonthOfferTicket({title, figures, plan, countdown, spotsLeft, eyebrow, actionLabel, onAction, testId, factsTestId}: {
	title: string;
	figures: OfferFirstMonthFigures;
	plan: PaidPlanId;
	countdown: CampaignCountdown | null;
	spotsLeft: number | null;
	eyebrow?: string;
	actionLabel: string;
	onAction: () => void;
	testId: string;
	factsTestId: string;
}) {
	const {banner, offerLadder, pricing} = useMessages();
	const campaign = banner.firstMonthOffer;
	return <div data-testid={testId}>
		<PeelTicket title={title} value={figures.amount} terms={pricing.welcome.firstMonth} eyebrow={eyebrow}
			compactValue stubLabel={offerLadder.gift.planLabel(pricing.planNames[plan])}
			claimed={false} onClaim={onAction} claimLabel={actionLabel}
			metadata={<>
				{spotsLeft !== null && <span data-testid={`${factsTestId}-spots-left`}>{campaign.spotsLeft(spotsLeft)}</span>}
				{countdown !== null && <span data-testid={`${factsTestId}-deadline`}>
					{campaign.endsIn(countdownDaysLabel(countdown, campaign))}
				</span>}
			</>}
		/>
	</div>;
}
